import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import MapPin from '@/images/map_pin.png'
import './index.less'

/**
 * 地图组件
 * 自动判定选择谷歌或高德地图
 *
 * @param {any} restaurants - 餐厅数据列表
 * @param {any} region - 区域（含国家）
 * @param {Function} tapMarkers - 点选标记回调
 * @param {Function} onMapChange - 地图变动回调
 * @param {boolean} autoFitBounds - 是否自动缩放地图到所有标记（默认 true）
 * @returns 地图组件
 */
const CollectionMap = forwardRef(({ restaurants, region, tapMarkers, onMapChange, autoFitBounds = true }, ref) => {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markerLayerRef = useRef(null)
  const currentBoundsRef = useRef(null) // 用于保存初始化的 bounds
  const skipNextMapChangeCountRef = useRef(0)

  useEffect(() => {
    const map = mapRef.current
    const el = containerRef.current
    if (!map || !el) return

    let rafId = null
    const scheduleInvalidate = () => {
      if (!mapRef.current) return
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        try {
          mapRef.current.invalidateSize({ animate: false })
        } catch (e) {
          // ignore
        }
      })
    }

    // Initial pass (helps when container is laid out after mount)
    scheduleInvalidate()

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleInvalidate) : null
    if (ro) ro.observe(el)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      if (ro) ro.disconnect()
    }
  })
  useEffect(() => {
    if (!region || Object.keys(region).length === 0) return

    const countryName = String(region?.country_name || '').toLowerCase()
    const inChina = countryName === 'china'

    const normal = L.layerGroup([
      L.tileLayer('https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_en&size=1&scale=1&style=8&x={x}&y={y}&z={z}', {
        maxZoom: 18,
        minZoom: 5,
        subdomains: '1234'
      })
    ])

    let googleMap = null
    if (countryName === 'hongkong') {
      googleMap = L.tileLayer.chinaProvider('Google.Normal.Map', {
        maxZoom: 18,
        minZoom: 5
      })
    } else {
      googleMap = L.tileLayer(`https://{s}.google.com/vt?lyrs=m&x={x}&y={y}&z={z}&hl=${region.country_name}`, {
        maxZoom: 18,
        minZoom: 5,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      })
    }
    const google = L.layerGroup([googleMap])

    if (!mapRef.current) {
      const map = L.map('leaflet-map', {
        center: [0, 0],
        zoom: 13,
        layers: [inChina ? normal : google],
        zoomControl: false,
        attributionControl: false
      })

      L.control.zoom({ position: 'topleft' }).addTo(map)

      map.on('click', () => {
        tapMarkers([])
      })
      map.on('zoomend', () => {
        if (skipNextMapChangeCountRef.current > 0) {
          skipNextMapChangeCountRef.current--
          return
        }
        triggerMapChange(map)
      })

      map.on('moveend', () => {
        if (skipNextMapChangeCountRef.current > 0) {
          skipNextMapChangeCountRef.current--
          return
        }
        triggerMapChange(map)
      })

      mapRef.current = map
    }

    function triggerMapChange(mapInstance) {
      if (typeof onMapChange === 'function') {
        const bounds = mapInstance.getBounds()
        const southwest = bounds.getSouthWest()
        const northeast = bounds.getNorthEast()
        onMapChange({
          zoom: mapInstance.getZoom(),
          bounds: {
            southwest: { lat: southwest.lat, lng: southwest.lng },
            northeast: { lat: northeast.lat, lng: northeast.lng }
          }
        })
      }
    }
  }, [region])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !region) return

    if (!restaurants || restaurants.length === 0) {
      // region 的 lat/lng 作为 中心点
      const centerLat = Number(region.lat)
      const centerLng = Number(region.lng)

      if (!isNaN(centerLat) && !isNaN(centerLng)) {
        skipNextMapChangeCountRef.current = 1 // 防止 moveend 被触发
        map.setView([centerLat, centerLng], 10) // 设置默认中心和缩放级别
      }
      return
    }

    if (markerLayerRef.current) {
      map.removeLayer(markerLayerRef.current)
    }

    const markers = L.markerClusterGroup({
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: false,
      iconCreateFunction: cluster =>
        L.divIcon({
          className: 'cluser-marker',
          html: `<b>${cluster.getChildCount()}</b>`
        })
    })

    const totalLatLng = {
      maxLat: -99999,
      minLat: 99999,
      maxLng: -99999,
      minLng: 99999
    }

    restaurants.forEach((item, index) => {
      let { lat, lon } = item.lat_lon
      lat = Number(lat) || region.lat
      lon = Number(lon) || region.lng

      if (lat && lon) {
        totalLatLng.maxLat = Math.max(lat, totalLatLng.maxLat)
        totalLatLng.minLat = Math.min(lat, totalLatLng.minLat)
        totalLatLng.maxLng = Math.max(lon, totalLatLng.maxLng)
        totalLatLng.minLng = Math.min(lon, totalLatLng.minLng)
      }

      const position = new L.LatLng(lat, lon)
      const marker = L.marker(position, {
        zIndexOffset: item.id,
        icon: L.icon({
          iconSize: [28, 32],
          iconUrl: MapPin
        })
      })

      marker.on('click', () => tapMarkers([index]))
      markers.addLayer(marker)
    })

    markers.on('clusterclick', a => {
      if (map.getZoom() < 18) {
        skipNextMapChangeCountRef.current = 2 // 跳过接下来的 zoomend 和 moveend
        a.layer.zoomToBounds({ padding: [20, 20] })
      } else {
        const childMarkers = a.layer.getAllChildMarkers()
        const markersIndex = childMarkers.map(marker => restaurants.findIndex(res => res.id === marker.options.zIndexOffset))
        tapMarkers(markersIndex)
      }
    })

    if (autoFitBounds && totalLatLng.maxLat !== -99999) {
      const bounds = [
        [totalLatLng.minLat, totalLatLng.minLng],
        [totalLatLng.maxLat, totalLatLng.maxLng]
      ]
      map.fitBounds(bounds, { padding: [20, 20] })
      currentBoundsRef.current = bounds
    }

    map.addLayer(markers)
    markerLayerRef.current = markers
  }, [restaurants, autoFitBounds])

  useImperativeHandle(ref, () => ({
    resetView: () => {
      const map = mapRef.current
      const bounds = currentBoundsRef.current
      if (map && bounds) {
        map.fitBounds(bounds, { padding: [20, 20] })
      }
    }
  }))

  return <div ref={containerRef} className='collection-map-view' id='leaflet-map'></div>
})

export default CollectionMap
